import {
  AfterContentChecked,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { addIcons } from 'ionicons';
import {
  addCircleSharp,
  bulbOutline,
  calendarSharp,
  chatbubbleOutline,
  checkmarkSharp,
  colorWandSharp,
  contractSharp,
  exitOutline,
  expandSharp,
  gameControllerSharp,
  helpSharp,
  keypad,
  languageSharp,
  librarySharp,
  menuSharp,
  playSkipForward,
  playSkipForwardOutline,
  printOutline,
  refreshSharp,
  removeCircleSharp,
  scanSharp,
  searchCircleOutline,
  settings,
  settingsOutline,
  shieldCheckmarkOutline,
  telescopeSharp,
  textSharp,
  trophySharp,
  warningOutline,
  contrastOutline,
  moonOutline,
  appsSharp,
} from 'ionicons/icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Button } from 'projects/tools/src/lib/shared-module/buttons/button.def';
import { Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { PuzzleAnalyticsService } from 'projects/tools/src/lib/shared-module/puzzle-analytics/puzzle-analytics.service';
import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { PuzzleTypeIdEnum } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-type-id.enum';
import { PuzzleGameState } from 'projects/tools/src/lib/store/puzzle-game.state';
import { PuzzleGameModel } from 'projects/tools/src/lib/store/puzzle-game.model';
import { ZoomIn, ZoomOut } from 'projects/tools/src/lib/store/puzzle-zoom.actions';
import { PuzzleGameTypesEnum } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-game-types.enum';
import { PuzzleHelpDialogService } from 'projects/tools/src/lib/shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { ButtonBarButton } from 'projects/tools/src/lib/shared-module/buttons/button-bar-button';
import { PuzzleHelpDialogComponent } from 'projects/tools/src/lib/shared-module/puzzle-help-dialog/puzzle-help-dialog.component';
import { PuzzleZoomService } from 'projects/tools/src/lib/shared-module/puzzle-zoom/puzzle-zoom.service';
import {
  DontShowSolutionDialogAgain,
  SetAutoZoomEnabled,
  SetDefinitionBarEnabled,
  SetHighlightRightAndWrongEnabled,
  SetShowErrorsImmediately,
  SetHighContrastEnabled,
  SetDarkModeEnabled,
  SetKeepKeyboardOpen,
  SetNativeKeyboardEnabled,
  SetSkipFilledFieldsEnabled,
  SetWordJumpEnabled,
  SetAutoMoveEnabled,
} from 'projects/tools/src/lib/store/puzzle-game.actions';
import {
  AlertController,
  IonBadge,
  IonButton,
  IonButtons,
  IonIcon,
  MenuController,
  ModalController,
} from '@ionic/angular/standalone';
import { PuzzleSweepstakeButtonComponent } from 'projects/tools/src/lib/shared-module/puzzle-sweepstake-button/puzzle-sweepstake-button.component';
import { PuzzleButtonCustomComponent } from 'projects/tools/src/lib/shared-module/puzzle-button-custom/puzzle-button-custom.component';
import { HelperFunctionsService } from 'projects/tools/src/lib/shared-module/helpers/helper-functions.service';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleUpdateNotificationsModel } from 'projects/tools/src/lib/store/puzzle-update-notifications.model';
import { PuzzleUpdateNotificationModalComponent } from 'projects/tools/src/lib/shared-module/puzzle-update-notification-modal/puzzle-update-notification-modal.component';
import { FeedbackService } from 'projects/tools/src/lib/shared-module/feedback/feedback.service';
import { FeedbackFeatureEnum } from 'projects/tools/src/lib/shared-module/feedback/feedback-feature.enum';
import {
  LetterOverwriteRestrictions,
  WordJumpOnRestrictions,
  DarkModes,
} from '../../../../tools/src/lib/store/puzzle-game-state.model';
import { TooltipModule } from 'ng2-tooltip-directive-major-angular-updates';
import { PuzzleFrameworkEvents } from '../../../../tools/src/lib/puzzle-framework-module/puzzle-framework-events.enum';

// ATTENTION: due to icons needing to be explicitly added for standalone components,
// this constant is used to add them in the components that may dynamically display
// icons from the button-bar buttons.
// After adding or changing a new definition below, add/change/remove the used icons here.
export const usedButtonBarIcons = {
  addCircleSharp,
  bulbOutline,
  calendarSharp,
  chatbubbleOutline,
  checkmarkSharp,
  colorWandSharp,
  contractSharp,
  exitOutline,
  expandSharp,
  gameControllerSharp,
  helpSharp,
  keypad,
  languageSharp,
  librarySharp,
  menuSharp,
  printOutline,
  refreshSharp,
  removeCircleSharp,
  scanSharp,
  searchCircleOutline,
  settings,
  settingsOutline,
  shieldCheckmarkOutline,
  telescopeSharp,
  textSharp,
  trophySharp,
  warningOutline,
  playSkipForwardOutline,
  playSkipForward,
  contrastOutline,
  moonOutline,
  appsSharp,
};

@Component({
  selector: 'app-puzzle-button-bar',
  templateUrl: './puzzle-button-bar.component.html',
  styleUrls: ['./puzzle-button-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    TooltipModule,
    TranslateModule,
    IonBadge,
    IonButton,
    IonButtons,
    IonIcon,
    PuzzleSweepstakeButtonComponent,
    PuzzleButtonCustomComponent,
  ],
})
export class PuzzleButtonBarComponent implements OnInit, OnDestroy, AfterContentChecked {
  isCrossword221 = false;
  public buttons: Array<ButtonBarButton> = [];
  public jokerCount: number;
  public helpBadge = '!';
  public helpBadgeHidden = true;
  public jokerBadgeHidden = false;
  public definitionBarEnabled = false;
  public highlightRightAndWrongEnabled = false;
  public showErrorsImmediately = false;
  public highContrastEnabled = false;
  public darkModeEnabled = false;
  public skipFilledFieldsEnabled = false;
  public wordJumpEnabled = false;
  public autoMoveEnabled = false;

  public menuBadge = 0;

  private browserWarningSubscription: Subscription;
  private puzzleGame$: Observable<any>;
  private puzzleGameSubscription: Subscription;

  showTooltips = true;

  private puzzleID: string;
  private isFullscreen: boolean;

  private gameType: PuzzleGameTypesEnum;

  @Input() allowVertical: boolean;

  public scaleButtons: string;
  public smallIcons = false;

  // Which button order should be chosen from config. Overridden by inheriting sidenav
  public buttonOrdersSelector = 'buttonBarOrder';
  private nativeKeyboard: boolean;
  private keepKeyboardOpen: boolean;
  private hideSolutionDialog: boolean;
  public disableShowSolutionButton = false;

  public updateNotifications: PuzzleUpdateNotificationsModel;
  public menuBadgeHidden = true;

  constructor(
    public translate: TranslateService,
    private cd: ChangeDetectorRef,
    private store: Store,
    public framework: PuzzleFrameworkService,
    private analytics: PuzzleAnalyticsService,
    private injector: Injector,
    private applicationRef: ApplicationRef,
    private helpDialogService: PuzzleHelpDialogService,
    private el: ElementRef,
    public zoom: PuzzleZoomService,
    private ionicAlert: AlertController,
    private ionicMenu: MenuController,
    protected ionicModal: ModalController,
    private helperFunctions: HelperFunctionsService,
    public leaderboard: LeaderboardService,
    public feedback: FeedbackService
  ) {
    addIcons(usedButtonBarIcons);
  }

  injectButtons() {
    this.helpDialogService.injectButtonBarButtons(this.buttons);
  }

  async openShowSolutionDialog() {
    if (this.hideSolutionDialog) {
      // if user clicked on hide before, just execute function
      this.executeShowSolution();
      return;
    }

    // else show dialog

    this.showTooltips = false;

    const translated = await Promise.all([
      this.translate.get('PUZZLE.show_solution_dialog.title').toPromise(),
      this.translate.get('PUZZLE.show_solution_dialog.message').toPromise(),
      this.translate.get('PUZZLE.show_solution_dialog.button.abort').toPromise(),
      this.translate.get('PUZZLE.show_solution_dialog.button.continue').toPromise(),
      this.translate.get('PUZZLE.show_solution_dialog.hide_dialog').toPromise(),
    ]);

    const alert = await this.ionicAlert.create({
      header: translated[0],
      message: translated[1],
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      inputs: [
        {
          name: 'hide',
          type: 'checkbox',
          value: 'hide',
          label: translated[4],
        },
      ],
      buttons: [
        {
          text: translated[2],
          cssClass: 'primary',
          role: 'cancel',
        },
        {
          text: translated[3],
          cssClass: 'primary',
          role: 'continue',
        },
      ],
    });

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    await alert.present();

    const result = await alert.onDidDismiss();

    this.ionicMenu.close('sidemenu');

    this.framework.resumeCurrentGame();

    if (result.role === 'continue') {
      // evaluate hide checkbox
      this.executeShowSolution();
      if (result.data && result.data.values && result.data.values.length && result.data.values[0] === 'hide') {
        this.store.dispatch(new DontShowSolutionDialogAgain({ puzzleID: this.puzzleID }));
      }
    }

    this.showTooltips = true;
  }

  private async executeShowSolution() {
    await this.ionicMenu.close('sidemenu');

    this.disableShowSolutionButton = true;
    this.cd.detectChanges();
    this.framework.buttonBarEmitButton(Button.ShowSolution);
    await this.helperFunctions.timeout(this.framework.puzzleFrameworkConfig.showSolutionTime + 2000);
    this.disableShowSolutionButton = false;
    this.cd.detectChanges();
  }

  async openDatePicker() {
    const translated = await Promise.all([
      this.translate.get('PUZZLE.date_picker_dialog.title').toPromise(),
      this.translate.get('PUZZLE.dialog.button.abort').toPromise(),
      this.translate.get('PUZZLE.dialog.button.select').toPromise(),
    ]);

    const puzzleName = this.framework.selectedPuzzleName;
    const puzzleConfig = this.framework.puzzleFrameworkConfig.puzzleFiles[puzzleName];

    let minDate = new Date();
    const maxDate = new Date();

    if (typeof puzzleConfig !== 'string') {
      minDate = this.framework.getFirstAllowedDate();
      if (
        this.framework.cheatCodeEnabled() ||
        this.framework.puzzleFrameworkConfig.urlDateParameterEnableFuture === true
      ) {
        maxDate.setDate(maxDate.getDate() + 100);
      }
    }

    const currentDate = this.framework.getCurrentDate();

    const alert = await this.ionicAlert.create({
      header: translated[0],
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      inputs: [
        {
          name: 'date',
          type: 'date',
          value: currentDate.toISOString().substr(0, 10),
          max: maxDate.toISOString().substr(0, 10),
          min: minDate.toISOString().substr(0, 10),
        },
      ],
      buttons: [
        { text: translated[1], cssClass: 'primary', role: 'cancel' },
        { text: translated[2], cssClass: 'primary', role: 'continue' },
      ],
    });

    await alert.present();
    const result = await alert.onDidDismiss();

    if (result.role !== 'continue' || !result.data || !result.data.values) {
      return;
    }

    const newDate = new Date(result.data.values.date);

    if (newDate.toDateString() === currentDate.toDateString()) {
      return;
    }

    const datePipe = new DatePipe('en-EN');
    const dateString = datePipe.transform(newDate, 'yyyy-MM-dd');
    const hashParameters = this.framework.getHashParameters();
    hashParameters.date = dateString;
    window.location.hash =
      '#' +
      Object.keys(hashParameters)
        .map((key) => key + '=' + hashParameters[key])
        .join('&');
  }

  async openPuzzlePicker() {
    const hashParameters = this.framework.getHashParameters();
    const currentPuzzle = hashParameters.puzzle || this.framework.configPuzzleName;

    const puzzleNames = Object.keys(this.framework.puzzleFrameworkConfig.puzzleFiles);

    const alert = await this.ionicAlert.create({
      header: 'Rätsel auswählen',
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      inputs: puzzleNames.map((name) => ({
        name,
        type: 'radio',
        label: name,
        value: name,
        checked: name === currentPuzzle,
      })),
      buttons: [
        { text: 'Zurück', cssClass: 'primary', role: 'cancel' },
        { text: 'Auswählen', cssClass: 'primary', role: 'continue' },
      ],
    });
    await alert.present();

    const result = await alert.onDidDismiss();

    if (result.role !== 'continue' || !result.data || !result.data.values || result.data.values === currentPuzzle) {
      return;
    }

    hashParameters.puzzle = result.data.values;
    window.location.hash =
      '#' +
      Object.keys(hashParameters)
        .map((key) => key + '=' + hashParameters[key])
        .join('&');
  }

  async openDifficultyPicker() {
    const difficultyValues = this.framework.puzzleFrameworkConfig.difficultyValues;
    const currentDifficulty = await this.store.selectOnce((state) => state.puzzleGameState.difficulty).toPromise();

    const translated = await Promise.all([
      this.translate.get('PUZZLE.difficulty_picker_dialog.title').toPromise(),
      this.translate.get('PUZZLE.dialog.button.abort').toPromise(),
      this.translate.get('PUZZLE.dialog.button.select').toPromise(),
    ]);

    const alert = await this.ionicAlert.create({
      header: translated[0],
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      inputs: difficultyValues.map((difficultyName) => ({
        name: difficultyName,
        type: 'radio',
        label: difficultyName,
        value: difficultyName,
        checked: currentDifficulty === difficultyName,
      })),
      buttons: [
        { text: translated[1], cssClass: 'primary', role: 'cancel' },
        { text: translated[2], cssClass: 'primary', role: 'continue' },
      ],
    });

    await alert.present();
    const result = await alert.onDidDismiss();

    if (result.role !== 'continue' || !result.data || !result.data.values || result.data.values === currentDifficulty) {
      return;
    }

    const hashParameters = this.framework.getHashParameters();

    // This is a duplicate of puzzle-changer-form-component.ts.difficultyChanged()
    // If we have the difficulty in the puzzle parameter, we need to chang it.
    if (this.framework.puzzleFrameworkConfig.urlDifficultyParameterInPuzzleParameter) {
      // Remove any difficulty parameter from the puzzle parameter.
      for (const difficulty of this.framework.puzzleFrameworkConfig.difficultyValues) {
        hashParameters.puzzle = hashParameters.puzzle.replace(`-${difficulty}`, '');
      }
      // Now set the new one.
      hashParameters.puzzle = `${hashParameters.puzzle}-${result.data.values}`;
    } else {
      // Otherwise we change the difficulty parameter.
      hashParameters.difficulty = result.data.values;
    }

    window.location.hash =
      '#' +
      Object.keys(hashParameters)
        .map((key) => key + '=' + hashParameters[key])
        .join('&');
  }

  async openLanguagePicker() {
    const translated = await Promise.all([
      this.translate.get('PUZZLE.language_picker_dialog.title').toPromise(),
      this.translate.get('PUZZLE.dialog.button.abort').toPromise(),
      this.translate.get('PUZZLE.dialog.button.select').toPromise(),
    ]);

    const languages = this.framework.puzzleFrameworkConfig.languagePickerValues || [];
    const currentLanguage = await this.store.selectOnce((state) => state.puzzleGameState.language).toPromise();
    const currentIndex = languages.map((lang) => lang.name).indexOf(currentLanguage);

    const alert = await this.ionicAlert.create({
      header: translated[0],
      backdropDismiss: true,
      cssClass: 'puzzle-alert',
      inputs: languages.map((lanugage, index) => ({
        name: lanugage.name,
        type: 'radio',
        label: lanugage.label,
        value: index,
        checked: lanugage.name === currentLanguage,
      })),
      buttons: [
        { text: translated[1], cssClass: 'primary', role: 'cancel' },
        { text: translated[2], cssClass: 'primary', role: 'continue' },
      ],
    });

    await alert.present();
    const result = await alert.onDidDismiss();

    if (result.role !== 'continue' || !result.data || result.data.values === currentIndex) {
      return;
    }

    const language = languages[result.data.values].name;
    const hashParameters = this.framework.getHashParameters();
    hashParameters.language = language;
    window.location.hash =
      '#' +
      Object.keys(hashParameters)
        .map((key) => key + '=' + hashParameters[key])
        .join('&');
  }

  // @HostListener('window:resize')
  checkAvailableHeight() {
    if (
      !this.allowVertical ||
      window.document.documentElement.offsetWidth / window.document.documentElement.offsetHeight <= 1
    ) {
      this.scaleButtons = 'scale(1)';
      return;
    }
    this.smallIcons = this.el.nativeElement.offsetHeight < this.el.nativeElement.scrollHeight;
    this.scaleButtons =
      'scale(' + (this.smallIcons ? this.el.nativeElement.offsetHeight / this.el.nativeElement.scrollHeight : 1) + ')';
  }

  /**
   * Title, tooltip and help copy for the two error-marking toggles.
   *
   * The shared `PUZZLE.*` strings talk about typed digits and completed words,
   * neither of which exists in bridges — there the same two settings mark
   * islands. Bridges therefore gets its own strings under `BRIDGES.settings.*`,
   * which the bridges help dialog quotes verbatim so the two always agree.
   */
  private bridgesAwareLabels(key: 'highlight_right_and_wrong' | 'show_errors_immediately') {
    if (this.gameType === PuzzleGameTypesEnum.BridgesGame) {
      return {
        title: `BRIDGES.settings.${key}.title`,
        // A hover tooltip has no room for the full rules: it says what the
        // setting does in one line and points at the places that have room.
        tooltip: `BRIDGES.settings.${key}.tooltip`,
        help: {
          title: `BRIDGES.settings.${key}.title`,
          text: `BRIDGES.settings.${key}.text`,
        },
      };
    }
    return {
      title: `PUZZLE.button_bar.buttons.${key}.title`,
      tooltip: `PUZZLE.button_bar.buttons.${key}.tooltip`,
      help: {
        title: `PUZZLE.button_bar.buttons.${key}.help.title`,
        text: `PUZZLE.button_bar.buttons.${key}.help.text`,
      },
    };
  }

  initButtons() {
    // Get button orders depending on the class setting for menu or button bar
    const buttonOrders = this.framework.puzzleFrameworkConfig[this.buttonOrdersSelector];

    let customBtnCtn = 0;

    this.buttons = [];
    for (const buttonOrder of buttonOrders) {
      let btn: ButtonBarButton = null;
      switch (buttonOrder) {
        case Button.Print:
          // Currently not available on puzzles without zoom directive
          if (this.gameType !== PuzzleGameTypesEnum.CrosswordPuzzle || this.helperFunctions.isMobileDevice()) {
            break;
          }
          btn = {
            name: 'Print',
            showInHelp: true,
            custom: false,
            function: () => this.zoom.zoomAndPrint.emit(),
            title: 'PUZZLE.button_bar.buttons.print.title',
            tooltip: 'PUZZLE.button_bar.buttons.print.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.print.help.title',
              text: 'PUZZLE.button_bar.buttons.print.help.text',
            },
            icon: 'print-outline',
          };
          break;
        case Button.SettingsMenu:
          btn = {
            name: 'SettingsMenu',
            showInHelp: true,
            custom: false,
            function: () => {
              this.openSettingsMenu();
            },
            title: 'PUZZLE.button_bar.buttons.settings_menu.title',
            tooltip: 'PUZZLE.button_bar.buttons.settings_menu.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.settings_menu.help.title',
              text: 'PUZZLE.button_bar.buttons.settings_menu.help.text',
            },
            icon: 'settings-outline',
          };
          break;
        case Button.Updates:
          btn = {
            name: 'Updates',
            showInHelp: true,
            badge: 'menuBadge',
            custom: false,
            function: () => {
              this.showUpdateNotificationDialog();
            },
            matBadgeColor: 'secondary',
            matBadgeHidden: 'menuBadgeHidden',
            title: 'PUZZLE.button_bar.buttons.updates.title',
            tooltip: 'PUZZLE.button_bar.buttons.updates.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.updates.help.title',
              text: 'PUZZLE.button_bar.buttons.updates.help.text',
            },
            icon: 'bulb-outline',
          };
          break;
        case Button.ShowSolution:
          if (this.gameType !== PuzzleGameTypesEnum.CrosswordPuzzle) {
            break;
          }
          btn = {
            name: 'ShowSolution',
            showInHelp: true,
            custom: false,
            divider: false,
            disabled: false,
            disabledProp: 'disableShowSolutionButton',
            icon: 'telescope-sharp',
            title: 'PUZZLE.button_bar.buttons.show_solution.title',
            tooltip: 'PUZZLE.button_bar.buttons.show_solution.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.show_solution.help.title',
              text: 'PUZZLE.button_bar.buttons.show_solution.help.text',
            },
            function: async (event: MouseEvent) => {
              // const mouseLeave = new Event('mouseout');
              // console.log(event.target.dispatchEvent(mouseLeave));
              // event.preventDefault();
              // event.stopPropagation()
              if (await this.leaderboard.willDisableLeaderboardRulesDialog('show_solution')) {
                this.openShowSolutionDialog();
              }
            },
          };
          break;
        case Button.SettingsDivider:
          if (this.gameType !== PuzzleGameTypesEnum.CrosswordPuzzle) {
            break;
          }
          btn = {
            name: 'SettingsDivider',
            showInHelp: false,
            custom: false,
            divider: true,
            disabled: true,
            // icon: 'settings',
            title: 'PUZZLE.button_bar.buttons.settings_divider.title',
            tooltip: '',
            help: {
              title: '',
              text: '',
            },
            function: () => {},
          };
          break;
        case Button.InfoDivider:
          btn = {
            name: 'InfoDivider',
            showInHelp: false,
            custom: false,
            divider: true,
            disabled: true,
            // icon: 'settings',
            title: 'PUZZLE.button_bar.buttons.info_divider.title',
            tooltip: '',
            help: {
              title: '',
              text: '',
            },
            function: () => {},
          };
          break;
        case Button.MenuDivider:
          btn = {
            name: 'MenuDivider',
            showInHelp: false,
            custom: false,
            divider: true,
            icon: 'menu-sharp',
            title: 'PUZZLE.button_bar.buttons.menu_divider.title',
            tooltip: '',
            help: {
              title: '',
              text: '',
            },
            function: () => {
              // this.framework.sidenavOpen = !this.framework.sidenavOpen;
              this.ionicMenu.close('sidemenu');
            },
          };
          break;
        case Button.Spacer:
          btn = {
            name: 'Spacer',
            showInHelp: false,
            custom: false,
            spacer: true,
          };
          break;
        case Button.MenuToggle:
          btn = {
            name: 'MenuToggle',
            showInHelp: false,
            custom: false,
            icon: 'menu-sharp',
            badge: 'menuBadge',
            matBadgeHidden: 'menuBadgeHidden',
            title: 'PUZZLE.button_bar.buttons.menu.title',
            tooltip: 'PUZZLE.button_bar.buttons.menu.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.menu.help.title',
              text: 'PUZZLE.button_bar.buttons.menu.help.text',
            },
            showText: true,
            function: () => {
              // this.framework.sidenavOpen = !this.framework.sidenavOpen;
              this.ionicMenu.open('sidemenu');
            },
          };
          break;
        case Button.NativeKeyboard:
          if (
            !this.framework.puzzleFrameworkConfig.nativeKeyboardAllowUserChange ||
            !this.helperFunctions.isMobileDevice() ||
            !this.framework.hasKeyboardInput
          ) {
            break;
          }
          btn = {
            name: 'NativeKeyboard',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'nativeKeyboard',
            icon: 'text-sharp',
            title: 'PUZZLE.button_bar.buttons.native_keyboard.title',
            tooltip: 'PUZZLE.button_bar.buttons.native_keyboard.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.native_keyboard.help.title',
              text: 'PUZZLE.button_bar.buttons.native_keyboard.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetNativeKeyboardEnabled({
                  nativeKeyboardEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.KeepKeyboardOpen:
          if (
            !this.framework.puzzleFrameworkConfig.keepKeyboardOpenAllowUserChange ||
            !this.helperFunctions.isMobileDevice() ||
            !this.framework.hasKeyboardInput
          ) {
            break;
          }
          btn = {
            name: 'KeepKeyboardOpen',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'keepKeyboardOpen',
            icon: 'keypad',
            title: 'PUZZLE.button_bar.buttons.keep_keyboard_open.title',
            tooltip: 'PUZZLE.button_bar.buttons.keep_keyboard_open.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.keep_keyboard_open.help.title',
              text: 'PUZZLE.button_bar.buttons.keep_keyboard_open.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetKeepKeyboardOpen({
                  keepKeyboardOpen: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleDefinitionBar:
          if (!this.framework.allowUserToggleDefinitionBar) {
            break;
          }
          btn = {
            name: 'ToggleDefinitionBar',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'definitionBarEnabled',
            icon: 'search-circle-outline',
            title: 'PUZZLE.button_bar.buttons.definition_bar.title',
            tooltip: 'PUZZLE.button_bar.buttons.definition_bar.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.definition_bar.help.title',
              text: 'PUZZLE.button_bar.buttons.definition_bar.help.text',
            },
            function: async (event) => {
              // this.zoom.autoZoomEnabled = event.checked;
              this.store.dispatch(
                new SetDefinitionBarEnabled({
                  definitionBarEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleHighlightRightAndWrong:
          if (
            [PuzzleGameTypesEnum.SudokuGame, PuzzleGameTypesEnum.WordGuessPuzzle].includes(this.gameType)
          ) {
            break;
          }
          btn = {
            name: 'ToggleHighlightRightAndWrong',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'highlightRightAndWrongEnabled',
            icon: 'warning-outline',
            // Bridges marks islands rather than words, so it needs its own wording.
            ...this.bridgesAwareLabels('highlight_right_and_wrong'),
            function: async (event) => {
              // this.zoom.autoZoomEnabled = event.checked;
              if (event.detail.checked) {
                if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
                  this.highlightRightAndWrongEnabled = false;
                  this.cd.detectChanges();
                  return;
                }
              }
              this.store.dispatch(
                new SetHighlightRightAndWrongEnabled({
                  highlightRightAndWrongEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleShowErrorsImmediately:
          if (![PuzzleGameTypesEnum.SudokuGame, PuzzleGameTypesEnum.BridgesGame].includes(this.gameType)) {
            break;
          }
          btn = {
            name: 'ToggleShowErrorsImmediately',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'showErrorsImmediately',
            icon: 'warning-outline',
            ...this.bridgesAwareLabels('show_errors_immediately'),
            function: async (event) => {
              // this.zoom.autoZoomEnabled = event.checked;
              if (event.detail.checked) {
                if (!(await this.leaderboard.willDisableLeaderboardRulesDialog('highlight_right_and_wrong'))) {
                  this.showErrorsImmediately = false;
                  this.cd.detectChanges();
                  return;
                }
              }
              this.store.dispatch(
                new SetShowErrorsImmediately({
                  showErrorsImmediately: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleHighContrast:
          btn = {
            name: 'ToggleHighContrast',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'highContrastEnabled',
            icon: 'contrast-outline',
            title: 'PUZZLE.button_bar.buttons.high_contrast.title',
            tooltip: 'PUZZLE.button_bar.buttons.high_contrast.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.high_contrast.help.title',
              text: 'PUZZLE.button_bar.buttons.high_contrast.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetHighContrastEnabled({
                  highContrastEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleDarkMode:
          btn = {
            name: 'ToggleDarkMode',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'darkModeEnabled',
            icon: 'moon-outline',
            title: 'PUZZLE.button_bar.buttons.dark_mode.title',
            tooltip: 'PUZZLE.button_bar.buttons.dark_mode.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.dark_mode.help.title',
              text: 'PUZZLE.button_bar.buttons.dark_mode.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetDarkModeEnabled({
                  darkModeEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleWordJump:
          if (this.isCrossword221) {
            break;
          }
          btn = {
            name: 'ToggleWordJump',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'wordJumpEnabled',
            icon: 'play-skip-forward-outline',
            title: 'PUZZLE.button_bar.buttons.word_jump.title',
            tooltip: 'PUZZLE.button_bar.buttons.word_jump.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.word_jump.help.title',
              text: 'PUZZLE.button_bar.buttons.word_jump.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetWordJumpEnabled({
                  wordJumpEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.AutoMove:
          btn = {
            name: 'ToggleAutoMove',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'autoMoveEnabled',
            icon: 'play-skip-forward-outline',
            title: 'PUZZLE.button_bar.buttons.auto_move.title',
            tooltip: this.highlightRightAndWrongEnabled
              ? 'PUZZLE.button_bar.buttons.auto_move.tooltip_highlight'
              : 'PUZZLE.button_bar.buttons.auto_move.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.auto_move.help.title',
              text: 'PUZZLE.button_bar.buttons.auto_move.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetAutoMoveEnabled({
                  autoMoveEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ToggleSkipFilledFields:
          btn = {
            name: 'ToggleSkipFilledFields',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'skipFilledFieldsEnabled',
            icon: 'exit-outline',
            title: 'PUZZLE.button_bar.buttons.skip_filled_fields.title',
            tooltip: 'PUZZLE.button_bar.buttons.skip_filled_fields.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.skip_filled_fields.help.title',
              text: 'PUZZLE.button_bar.buttons.skip_filled_fields.help.text',
            },
            function: async (event) => {
              this.store.dispatch(
                new SetSkipFilledFieldsEnabled({
                  skipFilledFieldsEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.DisableAutoZoom:
          if (this.framework.isZoomDisabled()) {
            break;
          }
          if (this.gameType !== PuzzleGameTypesEnum.CrosswordPuzzle) {
            break;
          }
          btn = {
            name: 'DisableAutoZoom',
            showInHelp: true,
            custom: false,
            slideToggleButton: true,
            slideToggleState: 'zoom.autoZoomEnabled',
            icon: 'scan-sharp',
            title: 'PUZZLE.button_bar.buttons.auto_zoom.title',
            tooltip: 'PUZZLE.button_bar.buttons.auto_zoom.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.auto_zoom.help.title',
              text: 'PUZZLE.button_bar.buttons.auto_zoom.help.text',
            },
            function: async (event) => {
              // this.zoom.autoZoomEnabled = event.checked;
              this.store.dispatch(
                new SetAutoZoomEnabled({
                  autoZoomEnabled: event.detail.checked,
                })
              );
            },
          };
          break;
        case Button.ZoomFull:
          if (this.framework.isZoomDisabled()) {
            break;
          }
          if (this.gameType === PuzzleGameTypesEnum.CrosswordPuzzle) {
            btn = {
              name: 'ZoomFull',
              showInHelp: true,
              custom: false,
              icon: 'scan-sharp',
              title: 'PUZZLE.button_bar.buttons.zoom_full.title',
              tooltip: 'PUZZLE.button_bar.buttons.zoom_full.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.zoom_full.help.title',
                text: 'PUZZLE.button_bar.buttons.zoom_full.help.text',
              },
              function: () => {
                // Zoom out to the full board.
                this.zoom.autoZoom.emit(null);
              },
            };
          }
          break;
        case Button.LanguagePicker:
          if (
            this.framework.puzzleFrameworkConfig.languagePickerValues &&
            this.framework.puzzleFrameworkConfig.languagePickerValues.length
          ) {
            btn = {
              name: 'LanguagePicker',
              showInHelp: true,
              custom: false,
              icon: 'language-sharp',
              title: 'PUZZLE.button_bar.buttons.language_picker.title',
              tooltip: 'PUZZLE.button_bar.buttons.language_picker.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.language_picker.help.title',
                text: 'PUZZLE.button_bar.buttons.language_picker.help.text',
              },
              function: () => {
                this.openLanguagePicker();
              },
            };
          }
          break;
        case Button.Sweepstake:
          if (
            this.framework.puzzleFrameworkConfig.solutionWordConfig.sweepstake.enabled &&
            this.framework.puzzleFrameworkConfig.solutionWordConfig.sweepstake.backendUrl
          ) {
            btn = {
              name: 'Sweepstake',
              showInHelp: true,
              sweepstakeButton: true,
              custom: false,
              icon: 'trophy-sharp',
              title: 'PUZZLE.button_bar.buttons.sweepstake.title',
              tooltip: 'PUZZLE.button_bar.buttons.menu.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.sweepstake.help.title',
                text: 'PUZZLE.button_bar.buttons.sweepstake.help.text',
              },
            };
          }
          break;
        case Button.PuzzlePicker:
          // TODO make selectable like this.framework.urlDateParameterEnabled
          // TODO translate text
          if (this.framework.cheatCodeEnabled()) {
            btn = {
              name: 'PuzzlePicker',
              showInHelp: true,
              custom: false,
              icon: 'library-sharp',
              title: 'PUZZLE.button_bar.buttons.puzzle_picker.title',
              tooltip: 'PUZZLE.button_bar.buttons.puzzle_picker.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.puzzle_picker.help.title',
                text: 'PUZZLE.button_bar.buttons.puzzle_picker.help.text',
              },
              function: () => {
                this.openPuzzlePicker();
              },
            };
          }
          break;
        case Button.DatePicker:
          if (this.framework.urlDateParameterEnabled || this.framework.cheatCodeEnabled()) {
            btn = {
              name: 'DatePicker',
              showInHelp: true,
              custom: false,
              icon: 'calendar-sharp',
              title: 'PUZZLE.button_bar.buttons.date_picker.title',
              tooltip: 'PUZZLE.button_bar.buttons.date_picker.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.date_picker.help.title',
                text: 'PUZZLE.button_bar.buttons.date_picker.help.text',
              },
              function: () => {
                this.openDatePicker();
              },
            };
          }
          break;
        case Button.AddAllNotes:
          if (this.gameType === PuzzleGameTypesEnum.SudokuGame) {
            btn = {
              name: 'AddAllNotes',
              showInHelp: true,
              custom: false,
              icon: 'apps-sharp', // 'videogame_asset', // 'storage', // games, engineering, psychology
              title: 'PUZZLE.button_bar.buttons.add_all_notes.title',
              tooltip: 'PUZZLE.button_bar.buttons.add_all_notes.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.add_all_notes.help.title',
                text: 'PUZZLE.button_bar.buttons.add_all_notes.help.text',
              },
              function: () => {
                this.framework.puzzleEvents.emit(PuzzleFrameworkEvents.ADD_ALL_NOTES);
                void this.ionicMenu.close('sidemenu');
              },
            };
          }
          break;
        case Button.DifficultyPicker:
          if (this.framework.urlDifficultyParameterEnabled) {
            btn = {
              name: 'DifficultyPicker',
              showInHelp: true,
              custom: false,
              icon: 'game-controller-sharp', // 'videogame_asset', // 'storage', // games, engineering, psychology
              title: 'PUZZLE.button_bar.buttons.difficulty_picker.title',
              tooltip: 'PUZZLE.button_bar.buttons.difficulty_picker.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.difficulty_picker.help.title',
                text: 'PUZZLE.button_bar.buttons.difficulty_picker.help.text',
              },
              function: () => {
                this.openDifficultyPicker();
              },
            };
          }
          break;
        case Button.Fullscreen:
          if (document.body.requestFullscreen) {
            btn = {
              name: 'Fullscreen',
              showInHelp: true,
              custom: false,
              icon: this.isFullscreen ? 'contract-sharp' : 'expand-sharp',
              title: 'PUZZLE.button_bar.buttons.fullscreen.title',
              tooltip: this.isFullscreen
                ? 'PUZZLE.button_bar.buttons.fullscreen.exit_tooltip'
                : 'PUZZLE.button_bar.buttons.fullscreen.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.fullscreen.help.title',
                text: 'PUZZLE.button_bar.buttons.fullscreen.help.text',
              },
              function: () => {
                this.toggleFullscreen();
                this.ionicMenu.close('sidemenu');
              },
            };
          }
          break;
        case Button.Privacy:
          // if (this.analytics.enabled === true) {
          btn = {
            name: 'Privacy',
            showInHelp: true,
            custom: false,
            icon: 'shield-checkmark-outline',
            title: 'PUZZLE.button_bar.buttons.privacy.title',
            tooltip: 'PUZZLE.button_bar.buttons.privacy.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.privacy.help.title',
              text: 'PUZZLE.button_bar.buttons.privacy.help.text',
            },
            function: () => {
              this.onPrivacyButtonClicked();
            },
          };
          // }
          break;
        case Button.Reset:
          btn = {
            name: 'Reset',
            showInHelp: true,
            custom: false,
            icon: 'refresh-sharp',
            title: 'PUZZLE.button_bar.buttons.restart.title',
            tooltip: 'PUZZLE.button_bar.buttons.restart.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.restart.help.title',
              text: 'PUZZLE.button_bar.buttons.restart.help.text',
            },
            function: () => {
              this.onResetBtnClicked();
            },
          };
          break;
        case Button.Check:
          if (
            [
              PuzzleGameTypesEnum.WordSearchPuzzle,
              PuzzleGameTypesEnum.QuizGame,
              PuzzleGameTypesEnum.WordGuessPuzzle,
            ].indexOf(this.gameType) === -1
          ) {
            btn = {
              name: 'Check',
              showInHelp: true,
              custom: false,
              icon: 'checkmark-sharp',
              title: 'PUZZLE.button_bar.buttons.check.title',
              tooltip: 'PUZZLE.button_bar.buttons.check.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.check.help.title',
                text: 'PUZZLE.button_bar.buttons.check.help.text',
              },
              function: () => {
                this.onCheckBtnClicked();
              },
            };
          }
          break;
        case Button.Language:
          btn = {
            name: 'Language',
            showInHelp: true,
            custom: false,
            icon: 'language-sharp',
            title: 'PUZZLE.button_bar.buttons.lang.title',
            tooltip: 'PUZZLE.lang',
            help: {
              title: 'PUZZLE.button_bar.buttons.lang.help.title',
              text: 'PUZZLE.button_bar.buttons.lang.help.text',
            },
            function: () => {
              this.onLanguageBtnClicked();
            },
          };
          break;
        case Button.Help:
          btn = {
            name: 'Help',
            showInHelp: false,
            custom: false,
            icon: 'help-sharp',
            title: 'PUZZLE.button_bar.buttons.help.title',
            tooltip: 'PUZZLE.button_bar.buttons.help.tooltip',
            help: {
              title: 'PUZZLE.button_bar.buttons.help.help.title',
              text: 'PUZZLE.button_bar.buttons.help.help.text',
            },
            badge: 'helpBadge',
            matBadgeHidden: 'helpBadgeHidden',
            function: () => {
              this.onHelpBtnClicked();
            },
          };
          break;
        case Button.Custom:
          customBtnCtn++;
          btn = {
            name: 'Custom',
            showInHelp: true,
            custom: true,
            customID: customBtnCtn,
          };
          break;
        case Button.ZoomIn:
          if (this.framework.isZoomDisabled()) {
            break;
          }
          if (
            [
              PuzzleGameTypesEnum.ImagePuzzle,
              PuzzleGameTypesEnum.SudokuGame,
              PuzzleGameTypesEnum.WordSearchPuzzle,
              PuzzleGameTypesEnum.QuizGame,
              PuzzleGameTypesEnum.WordGuessPuzzle,
            ].indexOf(this.gameType) === -1
          ) {
            btn = {
              name: 'ZoomIn',
              showInHelp: true,
              custom: false,
              icon: 'add-circle-sharp',
              title: 'PUZZLE.button_bar.buttons.zoom_in.title',
              tooltip: 'PUZZLE.button_bar.buttons.zoom_in.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.zoom_in.help.title',
                text: 'PUZZLE.button_bar.buttons.zoom_in.help.text',
              },
              function: () => {
                this.onZoomInClicked();
              },
            };
          }
          break;
        case Button.ZoomOut:
          if (this.framework.isZoomDisabled()) {
            break;
          }
          if (
            [
              PuzzleGameTypesEnum.ImagePuzzle,
              PuzzleGameTypesEnum.SudokuGame,
              PuzzleGameTypesEnum.WordSearchPuzzle,
              PuzzleGameTypesEnum.QuizGame,
              PuzzleGameTypesEnum.WordGuessPuzzle,
            ].indexOf(this.gameType) === -1
          ) {
            btn = {
              name: 'ZoomOut',
              showInHelp: true,
              custom: false,
              icon: 'remove-circle-sharp',
              title: 'PUZZLE.button_bar.buttons.zoom_out.title',
              tooltip: 'PUZZLE.button_bar.buttons.zoom_out.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.zoom_out.help.title',
                text: 'PUZZLE.button_bar.buttons.zoom_out.help.text',
              },
              function: () => {
                this.onZoomOutClicked();
              },
            };
          }
          break;
        case Button.Joker:
          if (
            [
              PuzzleGameTypesEnum.WordSearchPuzzle,
              PuzzleGameTypesEnum.QuizGame,
              PuzzleGameTypesEnum.ImagePuzzle,
              PuzzleGameTypesEnum.WordGuessPuzzle,
              // Bridges has no joker concept — the board only handles Check and
              // Reset. Without this it rendered a wand with a "0" badge that
              // only ever produced a "no jokers left" toast.
              PuzzleGameTypesEnum.BridgesGame,
            ].indexOf(this.gameType) === -1
          ) {
            btn = {
              name: 'Joker',
              showInHelp: true,
              custom: false,
              icon: 'color-wand-sharp',
              badge: 'jokerCount',
              matBadgeColor: 'accent',
              title: 'PUZZLE.button_bar.buttons.joker.title',
              tooltip: 'PUZZLE.button_bar.buttons.joker.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.joker.help.title',
                text: 'PUZZLE.button_bar.buttons.joker.help.text',
              },
              matBadgeHidden: 'jokerBadgeHidden',
              function: () => {
                this.onJokerButtonClicked();
              },
            };
          }
          break;
        case Button.Feedback:
          if (this.framework.puzzleFrameworkConfig.feedbackFeatures.includes(FeedbackFeatureEnum.General)) {
            btn = {
              name: 'Feedback',
              showInHelp: true,
              custom: false,
              function: () => {
                this.feedback.promptFeedbackModal(FeedbackFeatureEnum.General);
              },
              title: 'PUZZLE.button_bar.buttons.feedback.title',
              tooltip: 'PUZZLE.button_bar.buttons.feedback.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.feedback.help.title',
                text: 'PUZZLE.button_bar.buttons.feedback.help.text',
              },
              icon: 'chatbubble-outline',
            };
          }
          break;
        case Button.LeaderboardReview:
          if (this.framework.cheatCodeEnabled()) {
            btn = {
              name: 'LeaderboardReview',
              showInHelp: false,
              custom: false,
              function: () => {
                this.leaderboard.promptReviewModal();
              },
              title: 'PUZZLE.button_bar.buttons.leaderboard_review.title',
              tooltip: 'PUZZLE.button_bar.buttons.leaderboard_review.tooltip',
              help: {
                title: 'PUZZLE.button_bar.buttons.leaderboard_review.help.title',
                text: 'PUZZLE.button_bar.buttons.leaderboard_review.help.text',
              },
              icon: 'shield-checkmark-outline',
            };
          }
          break;
      }
      if (btn !== null) {
        this.buttons.push(btn);
      }
    }
  }

  ngOnInit() {
    this.framework.puzzleConfigLoaded$.subscribe((puzzleType) => {
      const before = this.isCrossword221;
      this.isCrossword221 = puzzleType === PuzzleTypeIdEnum.CROSSWORD_221;
      if (before !== this.isCrossword221) {
        this.initButtons();
      }
      if (!this.cd['destroyed']) {
        this.cd.detectChanges();
      }
    });
    this.browserWarningSubscription = this.framework.browserWarning$.subscribe((showWarning) => {
      if (showWarning) {
        this.helpBadgeHidden = false;
        if (!this.cd[`destroyed`]) {
          this.cd.detectChanges();
        }
      }
    });

    this.helpDialogService.helpDialogRegistered.subscribe(() => {
      this.injectButtons();
    });

    this.store
      .select((state) => state.puzzleUpdateNotifications)
      .subscribe((n) => {
        this.updateNotifications = n;
        this.menuBadge = n.unreadUpdateNotifications.length;
        this.menuBadgeHidden = !this.updateNotifications.hasUpdates;
        this.cd.detectChanges();
      });

    this.store
      .select((state) => state.puzzleGameState.definitionBarEnabled)
      .subscribe((enabled: boolean) => {
        this.definitionBarEnabled = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.highlightRightAndWrongEnabled)
      .subscribe((enabled: boolean) => {
        this.highlightRightAndWrongEnabled = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.showErrorsImmediately)
      .subscribe((enabled: boolean) => {
        this.showErrorsImmediately = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.highContrastEnabled)
      .subscribe((enabled: boolean) => {
        this.highContrastEnabled = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.darkMode)
      .subscribe((darkMode: DarkModes) => {
        this.darkModeEnabled = darkMode === DarkModes.Dark;
      });

    // TODO this is wrong?
    this.store
      .select((state) => state.puzzleGameState.letterOverrideRestriction)
      .subscribe((restriction) => {
        // letterOverrideRestrictions has three states, but this toggle is simplified to on and off
        this.skipFilledFieldsEnabled = restriction !== undefined && restriction !== LetterOverwriteRestrictions.None;
      });

    // Multiple selectors: https://github.com/ngrx/platform/issues/2839#issuecomment-750319660
    this.store
      .select((state) => ({
        jumpOn: state.puzzleGameState.wordJumpOnRestriction,
        jumpTo: state.puzzleGameState.wordJumpToRestriction,
      }))
      .subscribe((restriction) => {
        this.wordJumpEnabled = restriction.jumpOn !== WordJumpOnRestrictions.None;
      });

    // Multiple selectors: https://github.com/ngrx/platform/issues/2839#issuecomment-750319660
    this.store
      .select((state) => ({
        jumpOn: state.puzzleGameState.wordJumpOnRestriction,
        jumpTo: state.puzzleGameState.wordJumpToRestriction,
        autoZoom: state.puzzleGameState.autoZoomEnabled,
      }))
      .subscribe((restriction) => {
        this.autoMoveEnabled = restriction.jumpOn !== WordJumpOnRestrictions.None;
      });

    this.store
      .select((state) => state.puzzleGameState.keepKeyboardOpen)
      .subscribe((enabled) => {
        this.keepKeyboardOpen = enabled;
      });

    this.store
      .select((state) => state.puzzleGameState.nativeKeyboard)
      .subscribe((enabled) => {
        this.nativeKeyboard = enabled;
      });

    this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID) => {
        if (puzzleID === '') {
          return;
        }

        if (this.puzzleGameSubscription) {
          this.puzzleGameSubscription.unsubscribe();
        }

        this.initButtons();

        this.puzzleID = puzzleID;

        this.puzzleGame$ = this.store.select(PuzzleGameState.getByID(puzzleID)) as Observable<PuzzleGameModel>;
        this.puzzleGameSubscription = this.puzzleGame$.subscribe((puzzleGame: PuzzleGameModel) => {
          if (!puzzleGame) {
            return;
          }

          this.hideSolutionDialog = puzzleGame.hideShowSolutionDialog;

          this.jokerCount = puzzleGame.jokerCount;

          let shouldInit = false;

          const gameType = puzzleGame.gameType as PuzzleGameTypesEnum;
          if (this.gameType !== gameType) {
            this.gameType = gameType;
            shouldInit = true;
          }

          if (shouldInit) {
            this.initButtons();
          }

          if (!this.cd[`destroyed`]) {
            this.cd.detectChanges();
          }
        });
      });

    const observer = new ResizeObserver((entries) => {
      this.checkAvailableHeight();
    });
    observer.observe(this.el.nativeElement);
  }

  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.body.requestFullscreen().then(() => {
        // console.log(document.fullscreenEnabled);
        this.isFullscreen = !!document.fullscreenElement;
        // if (!this.cd['destroyed']) {
        //   this.cd.detectChanges();
        // }
        this.initButtons();
      });
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange() {
    if (this.isFullscreen !== !!document.fullscreenElement) {
      this.isFullscreen = !!document.fullscreenElement;
      this.initButtons();
    }
  }

  onPrivacyButtonClicked() {
    this.framework.buttonBarEmitButton(Button.Privacy);
  }

  async onJokerButtonClicked() {
    // this.analytics.sendEvent('joker', 'button_bar');
    // check if user wants to (not) follow leaderboard rules
    if (await this.leaderboard.willDisableLeaderboardRulesDialog('joker')) {
      if (this.jokerCount > 0) {
        this.framework.buttonBarEmitButton(Button.Joker);
      } else {
        this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.no_jokers_left');
      }
    }
  }

  onZoomInClicked() {
    // this.analytics.sendEvent('zoom in', 'button_bar');
    this.store.dispatch(
      new ZoomIn({
        factor: 25,
        limit: this.framework.puzzleFrameworkConfig.zoomInLimit,
      })
    );
  }

  onZoomOutClicked() {
    // this.analytics.sendEvent('zoom out', 'button_bar');
    this.store.dispatch(
      new ZoomOut({
        factor: 25,
        limit: this.framework.puzzleFrameworkConfig.zoomOutLimit,
      })
    );
  }

  async onHelpBtnClicked() {
    // // this.analytics.sendEvent('swedish-help', 'button_bar');
    // // this.framework.buttonBarEmitButton(Button.Help);
    // this.dialog.open(PuzzleHelpDialogComponent, {
    //   closeOnNavigation: false,
    //   maxWidth: 800,
    //   data: {}
    // });

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    const modal = await this.ionicModal.create({
      component: PuzzleHelpDialogComponent,
      cssClass: 'puzzle-modal',
      componentProps: {},
    });
    await modal.present();
    await modal.onDidDismiss();
    this.framework.resumeCurrentGame();
  }

  async onResetBtnClicked() {
    const translated = await Promise.all([
      this.translate.get('PUZZLE.reset_dialog.title').toPromise(),
      this.translate.get('PUZZLE.reset_dialog.text').toPromise(),
      this.translate.get('PUZZLE.dialog.button.no').toPromise(),
      this.translate.get('PUZZLE.dialog.button.yes').toPromise(),
    ]);

    const alert = await this.ionicAlert.create({
      header: translated[0],
      message: translated[1],
      backdropDismiss: false,
      cssClass: 'puzzle-alert',
      buttons: [
        {
          text: translated[2],
          cssClass: 'primary',
          role: 'cancel',
        },
        {
          text: translated[3],
          cssClass: 'primary',
          role: 'reset',
        },
      ],
    });

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    await alert.present();

    const result = await alert.onDidDismiss();

    this.ionicMenu.close('sidemenu');

    this.framework.resumeCurrentGame();

    if (result.role === 'reset') {
      this.framework.buttonBarEmitButton(Button.Reset);
    }
  }

  async onCheckBtnClicked() {
    // this.analytics.sendEvent('check', 'button_bar');
    if (await this.leaderboard.willDisableLeaderboardRulesDialog('check')) {
      this.framework.buttonBarEmitButton(Button.Check);
    }
    // this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
  }

  onLanguageBtnClicked() {
    //
  }

  onPreviewBtnClicked() {
    // inspiration taken from https://material.angular.io/components/dialog/overview
  }

  ngOnDestroy(): void {
    if (this.puzzleGameSubscription) {
      this.puzzleGameSubscription.unsubscribe();
    }
    if (this.browserWarningSubscription) {
      this.browserWarningSubscription.unsubscribe();
    }
  }

  ngAfterContentChecked(): void {
    this.checkAvailableHeight();
  }

  async showUpdateNotificationDialog() {
    const modal = await this.ionicModal.create({
      component: PuzzleUpdateNotificationModalComponent,
      backdropDismiss: true,
      cssClass: 'puzzle-modal',
    });

    await modal.present();
    await modal.onDidDismiss();
  }

  openSettingsMenu() {
    this.framework.buttonBarEmitButton(Button.SettingsMenu);
  }
}
